import { Dialog } from '@angular/cdk/dialog';
import { inject } from '@angular/core';
import { Router, type CanActivateFn } from '@angular/router';
import type { MenuItem } from '@paytms/shared';
import {
  ChangePasswordDialog,
  type ChangePasswordDialogData,
} from '../../shell/change-password-dialog';
import { AuthService } from './auth.service';

/**
 * 지금 떠 있는 강제 변경 다이얼로그 — 가드는 이동마다 다시 평가되므로(로그인 → /main,
 * 주소창 직접 입력, 새로고침 복원 뒤 첫 이동) 하나만 띄우고 나머지는 막기만 한다.
 * 모듈 변수인 이유: 가드는 함수라 상태를 둘 곳이 없고, 다이얼로그는 앱에 하나뿐이다.
 */
let forcedChangeOpen = false;

/**
 * 임시 비밀번호로 로그인한 사용자에게 **닫을 수 없는** 비밀번호 변경 창을 띄운다
 * (2026-09-15, 비밀번호 찾기 1안). 서버가 허용 라우트 5종·`/menus` 밖 전부를 403
 * `PASSWORD_CHANGE_REQUIRED`로 막으므로, 화면 쪽은 보호 라우트를 열지 않고 이 창부터 채운다 —
 * 열어 두면 화면마다 403 토스트가 뜨는 "고장난 화면"이 된다.
 *
 * 변경이 끝나면(성공으로만 닫힌다) 메모리의 플래그를 내리고 `/main`으로 간다. 서버가 다른
 * 세션만 폐기하고 현재 세션은 유지하므로 재로그인은 필요 없다.
 */
function openForcedPasswordChange(
  auth: AuthService,
  dialog: Dialog,
  router: Router,
): void {
  if (forcedChangeOpen) {
    return;
  }
  forcedChangeOpen = true;
  const ref = dialog.open<boolean, ChangePasswordDialogData>(ChangePasswordDialog, {
    data: { forced: true },
    disableClose: true, // ESC·백드롭 클릭으로 못 닫는다 — 닫는 길은 변경 성공뿐
    backdropClass: 'pk-dialog-backdrop',
    ariaLabel: '임시 비밀번호 변경',
  });
  ref.closed.subscribe((changed) => {
    forcedChangeOpen = false;
    if (changed !== true) {
      // 세션이 끊겨 창이 스스로 닫힌 경우(만료·[로그아웃]·다른 탭 로그아웃) — 플래그는 건드리지
      // 않는다. 다음 로그인이 새 값을 가져오고, 아직 살아 있는 세션이면 다음 이동에서 다시 띄운다
      return;
    }
    auth.clearMustChangePassword();
    void router.navigate(['/main']);
  });
}

/** 인증 필요 라우트 — 미인증이면 /login으로 (복원은 provideAppInitializer가 가드 평가 전에 완료) */
export const authGuard: CanActivateFn = () => {
  const auth = inject(AuthService);
  const router = inject(Router);
  // 로그아웃 확정(revoke 응답) 전까지는 토큰이 살아 있다 — 그 사이 뒤로가기를 누르면 보호
  // 화면이 유효 토큰으로 열리고, 뒤늦은 정리는 재이동을 하지 않아 데이터를 띄운 채 남는다.
  // guestGuard의 `loggingOut` 통과와 대칭으로 여기서 닫는다 (공용 PC 전제)
  if (auth.loggingOut()) {
    return router.createUrlTree(['/login']);
  }
  if (!auth.isAuthenticated()) {
    return router.createUrlTree(['/login']);
  }
  if (auth.user()?.mustChangePassword === true) {
    // 임시 비밀번호 상태 — 보호 화면 대신 강제 변경 창. 이동은 취소한다(false): UrlTree로
    // 어딘가로 보내면 그 화면의 가드가 다시 여기로 와 루프가 된다
    openForcedPasswordChange(auth, inject(Dialog), router);
    return false;
  }
  return true;
};

/**
 * 조회 권한이 있는 첫 화면의 경로 — 권한 없는 화면에서 되돌아갈 자리.
 *
 * 메인을 우선하되, **메인이 막혀 있을 수도 있다고 전제한다.** 이 자리에는 원래
 * "메인은 3계층 기본 허용이라 되돌아갈 자리가 항상 있다"고 적혀 있었는데,
 * 차단 오버라이드가 그 전제를 깨서 `/main` → 가드 실패 → `/main` 무한 루프가 났다(NG04016).
 * 서버가 MAIN을 차단 불가로 잠갔지만, 가드가 스스로 루프에 빠지지 않는 것과는 별개다 —
 * **되돌아갈 자리는 가정하지 말고 실제 권한에서 찾는다.**
 *
 * 여기서 고른 경로는 반드시 VIEW 권한을 확인한 화면이라 그 화면의 가드가 다시 막지 않는다.
 */
function firstAllowedPath(auth: AuthService): string | null {
  if (auth.hasPermission('MAIN', 'VIEW')) {
    return '/main';
  }
  const find = (nodes: readonly MenuItem[]): string | null => {
    for (const node of nodes) {
      if (node.isScreen && node.url !== null && auth.hasPermission(node.menuCode, 'VIEW')) {
        return node.url;
      }
      const found = find(node.children);
      if (found !== null) {
        return found;
      }
    }
    return null;
  };
  return find(auth.menus());
}

/**
 * 계층 노출(PAGE관리 `menu_visibility`) 여부 — 서버가 걸러 보낸 메뉴 트리에 그 메뉴가 있는가.
 * 권한(VIEW)과 노출은 다른 축이라, Agent에게 사용자관리 VIEW 기본 권한이 있어도 PAGE관리에서
 * 노출을 껐으면 URL 직접 진입도 막아야 한다 (운영 QA 9/7 BUG-1: /users·/notices가 Agent에게 열렸다).
 */
function isMenuVisible(nodes: readonly MenuItem[], menuCode: string): boolean {
  return nodes.some(
    (node) => node.menuCode === menuCode || isMenuVisible(node.children, menuCode),
  );
}

/**
 * 화면 진입 권한 가드 — 그 메뉴의 VIEW 권한이 없거나 계층에 노출되지 않은 화면이면
 * 갈 수 있는 화면으로 되돌린다.
 *
 * 사이드바에 안 뜨는 화면도 URL을 직접 치면 열렸다. 서버가 API를 막으므로 데이터가
 * 새지는 않았지만, 빈 화면과 403 토스트만 남아 "고장난 화면"으로 보였다.
 *
 * 갈 수 있는 화면이 하나도 없으면 이동을 취소한다(false). 셸(GNB·로그아웃)은 남으므로
 * 사용자가 갇히지 않고, 어디로도 보낼 수 없는 상태에서 억지 리다이렉트로 루프를 만드는
 * 것보다 낫다. 정상 운영에서는 나올 수 없는 조합이라 콘솔에 근거를 남긴다.
 */
export const permissionGuard =
  (menuCode: string): CanActivateFn =>
  () => {
    const auth = inject(AuthService);
    const router = inject(Router);
    if (auth.hasPermission(menuCode, 'VIEW') && isMenuVisible(auth.menus(), menuCode)) {
      return true;
    }
    const fallback = firstAllowedPath(auth);
    if (fallback === null) {
      console.warn(`[auth] ${menuCode} 조회 권한이 없고, 대신 열 수 있는 화면도 없습니다.`);
      return false;
    }
    return router.createUrlTree([fallback]);
  };

/** 비로그인 전용 라우트(로그인 화면) — 이미 인증돼 있으면 메인으로 */
export const guestGuard: CanActivateFn = () => {
  const auth = inject(AuthService);
  const router = inject(Router);
  // 로그아웃 이동 중이면 토큰이 아직 살아 있어도 통과시킨다 — 미저장 확인창을 먼저 띄우려고
  // 세션 정리를 이동 뒤로 미루기 때문이다(AuthService.logout 주석). 여기서 되돌리면
  // 만료로 끊긴 사용자가 로그인 화면 대신 메인으로 돌아온다 (실측 9/8)
  if (auth.loggingOut()) {
    return true;
  }
  return auth.isAuthenticated() ? router.createUrlTree(['/main']) : true;
};
